import React, { useState, useEffect, useCallback } from 'react';
import './InvoiceArchive.css';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import { ArchivFilter, archivQuery, statusKlasse, vormonat, monatsZeitraum, dateinameAusHeader } from '../../utils/rechnungsarchiv';

/**
 * T-098 (v1.8.45, Session 40, Kundin-Mail 01.10.2026): Rechnungsarchiv für Verwaltung und Buchhaltung (Anlage v1.4 Abschnitt 2.7
 * Punkt 9). Alle Belege eines Zeitraums (Buchungsrechnungen, Gutscheinrechnungen, Barrechnungen) als Übersicht, CSV-Export für
 * Excel und ZIP mit allen PDFs. Zeitraum nach Rechnungsdatum (Ortszeit Deutschland).
 */

interface Beleg {
  quelle: string;
  id: number;
  nummer: string;
  art: string;
  datum: string;
  faellig?: string | null;
  empfaenger: string;
  bezug?: string | null;
  fahrtdatum?: string | null;
  zahlungsart?: string | null;
  brutto: number;
  bezahlt: number;
  offen: number;
  status: string;
}

interface ArchivAntwort {
  belege: Beleg[];
  anzahl: number;
  summeBrutto: number;
  summeBezahlt: number;
  summeOffen: number;
  maxPdfJeZip: number;
}

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const formatCurrency = (n: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

const InvoiceArchive: React.FC = () => {
  const start = vormonat(new Date());
  const startZeitraum = monatsZeitraum(start.jahr, start.monat);
  const [filter, setFilter] = useState<ArchivFilter>({
    modus: 'monat', von: startZeitraum.von, bis: startZeitraum.bis, monat: start.monat, jahr: start.jahr, quelle: '', status: '', suche: ''
  });
  const [daten, setDaten] = useState<ArchivAntwort | null>(null);
  // Review m7: Downloads immer mit dem Filter der angezeigten Tabelle
  const [geladen, setGeladen] = useState<ArchivFilter | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [download, setDownload] = useState<'csv' | 'zip' | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const jahre = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 2 + i);

  const laden = useCallback(async (f: ArchivFilter) => {
    setLaedt(true);
    setFehler(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/invoice-archive${archivQuery(f)}`);
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      setDaten(await response.json());
      setGeladen(f);
    } catch (err: any) {
      setFehler(`Fehler beim Laden des Rechnungsarchivs: ${err.message}`);
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    laden(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const herunterladen = async (pfad: string, vorgabe: string, art: 'csv' | 'zip' | null) => {
    setDownload(art);
    setFehler(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/invoice-archive${pfad}`);
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = dateinameAusHeader(response.headers.get('Content-Disposition'), vorgabe);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setFehler(`Download fehlgeschlagen: ${err.message}`);
    } finally {
      setDownload(null);
    }
  };

  const zeitraumName = (f: ArchivFilter) => {
    if (f.modus === 'monat') return `${f.jahr}-${String(f.monat).padStart(2, '0')}`;
    if (f.modus === 'zeitraum') return `${f.von}_bis_${f.bis}`;
    return 'alle';
  };
  // Review m8: Zeitraum nur mit Beginn und Ende
  const zeitraumUnvollstaendig = filter.modus === 'zeitraum' && (!filter.von || !filter.bis);
  const filterGeaendert = !!geladen && archivQuery(geladen) !== archivQuery(filter);

  const setze = (teil: Partial<ArchivFilter>) => setFilter(prev => ({ ...prev, ...teil }));
  const zuVieleFuerZip = !!daten && daten.anzahl > daten.maxPdfJeZip;

  return (
    <div className="archiv-management">
      <div className="archiv-header">
        <h1>Rechnungsarchiv</h1>
        <div className="archiv-actions">
          <button className="archiv-btn-secondary" onClick={() => window.print()} disabled={!daten}>Drucken</button>
          <button className="archiv-btn-secondary" onClick={() => geladen && herunterladen(`/export.csv${archivQuery(geladen)}`, `Rechnungen_${zeitraumName(geladen)}.csv`, 'csv')}
            disabled={!daten || download !== null}>
            {download === 'csv' ? 'Export läuft ...' : 'Exportieren (CSV/Excel)'}
          </button>
          <button className="archiv-btn-primary" onClick={() => geladen && herunterladen(`/pdfs.zip${archivQuery(geladen)}`, `Rechnungen_${zeitraumName(geladen)}.zip`, 'zip')}
            disabled={!daten || daten.anzahl === 0 || zuVieleFuerZip || download !== null}
            title={zuVieleFuerZip ? `Höchstens ${daten?.maxPdfJeZip} Belege je Download, bitte Zeitraum verkleinern` : 'Alle PDFs des Zeitraums als ZIP mit Liste'}>
            {download === 'zip' ? 'PDFs werden erstellt ...' : 'PDFs herunterladen (ZIP)'}
          </button>
        </div>
      </div>

      {fehler && (
        <div className="archiv-error">
          {fehler}
          <button onClick={() => setFehler(null)}>✕</button>
        </div>
      )}

      <div className="archiv-filter-bar">
        <label className="archiv-radio">
          <input type="radio" checked={filter.modus === 'alle'} onChange={() => setze({ modus: 'alle' })} /> Alle
        </label>
        <label className="archiv-radio">
          <input type="radio" checked={filter.modus === 'zeitraum'} onChange={() => setze({ modus: 'zeitraum' })} />
          <input type="date" value={filter.von} onChange={e => setze({ modus: 'zeitraum', von: e.target.value })} />
          <span>bis</span>
          <input type="date" value={filter.bis} onChange={e => setze({ modus: 'zeitraum', bis: e.target.value })} />
        </label>
        <label className="archiv-radio">
          <input type="radio" checked={filter.modus === 'monat'} onChange={() => setze({ modus: 'monat' })} />
          <select value={filter.monat} onChange={e => setze({ modus: 'monat', monat: Number(e.target.value) })}>
            {MONATE.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={filter.jahr} onChange={e => setze({ modus: 'monat', jahr: Number(e.target.value) })}>
            {jahre.map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        </label>
        <select value={filter.quelle} onChange={e => setze({ quelle: e.target.value })}>
          <option value="">Alle Belegarten</option>
          <option value="Buchung">Buchungsrechnungen</option>
          <option value="Gutschein">Gutscheinrechnungen</option>
          <option value="Bar">Barrechnungen</option>
        </select>
        <select value={filter.status} onChange={e => setze({ status: e.target.value })}>
          <option value="">Alle Status</option>
          <option value="Bezahlt">Bezahlt</option>
          <option value="Teilweise bezahlt">Teilweise bezahlt</option>
          <option value="Offen">Offen</option>
          <option value="Storniert">Storniert</option>
        </select>
        <input className="archiv-search" type="text" placeholder="Rechnungsnr., Empfänger, Buchung ..." value={filter.suche}
          onChange={e => setze({ suche: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') laden(filter); }} />
        <button className="archiv-btn-primary" onClick={() => laden(filter)} disabled={laedt || zeitraumUnvollstaendig}>Suche</button>
      </div>

      {zeitraumUnvollstaendig && <div className="archiv-hint">Bitte Beginn und Ende des Zeitraums angeben.</div>}
      {filterGeaendert && !laedt && <div className="archiv-hint">Filter geändert: Bitte "Suche" klicken, Export und Download beziehen sich auf die angezeigte Liste.</div>}

      {laedt ? (
        <div className="archiv-loading">Lädt Rechnungsarchiv ...</div>
      ) : daten && (
        <>
          <table className="archiv-table">
            <thead>
              <tr>
                <th></th>
                <th>Rechnungsnr.</th>
                <th>Datum</th>
                <th>Art</th>
                <th>Empfänger</th>
                <th>Buchung/Gutschein</th>
                <th>Fahrt</th>
                <th>Zahlungsart</th>
                <th className="archiv-col-amount">Brutto</th>
                <th className="archiv-col-amount">Offen</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {daten.belege.map(b => (
                <tr key={`${b.quelle}-${b.id}`} className={b.status === 'Storniert' ? 'archiv-row-storniert' : ''}>
                  <td><span className={`archiv-status ${statusKlasse(b.status)}`} title={b.status}></span></td>
                  <td className="archiv-number">{b.nummer}</td>
                  <td>{formatDate(b.datum)}</td>
                  <td>{b.art}</td>
                  <td>{b.empfaenger}</td>
                  <td>{b.bezug || ''}</td>
                  <td>{formatDate(b.fahrtdatum)}</td>
                  <td>{b.zahlungsart || ''}</td>
                  <td className="archiv-col-amount">{formatCurrency(b.brutto)}</td>
                  <td className="archiv-col-amount">{b.offen > 0 ? formatCurrency(b.offen) : ''}</td>
                  <td>
                    <button className="archiv-btn-action" title="PDF herunterladen"
                      onClick={() => herunterladen(`/${encodeURIComponent(b.quelle)}/${b.id}/pdf`, `${b.nummer}.pdf`, null)}>PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8}>{daten.anzahl} Belege (Summen ohne Stornierte)</td>
                <td className="archiv-col-amount">{formatCurrency(daten.summeBrutto)}</td>
                <td className="archiv-col-amount">{formatCurrency(daten.summeOffen)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          {daten.anzahl === 0 && <div className="archiv-empty">Keine Belege im gewählten Zeitraum.</div>}
          <div className="archiv-legend">
            <span><span className="archiv-status archiv-status-bezahlt"></span> Bezahlt</span>
            <span><span className="archiv-status archiv-status-teilweise"></span> Teilweise bezahlt</span>
            <span><span className="archiv-status archiv-status-offen"></span> Offen</span>
            <span><span className="archiv-status archiv-status-storniert"></span> Storniert</span>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceArchive;
